import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useNavigate } from 'react-router'

import { AskCai } from '@/components/AskCai/AskCai'
import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { useCaiDocked } from '@/components/sidebar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ResponsiveTable } from '@/components/ui/responsive-table'
import { SkeletonRows } from '@/components/ui/skeleton'
import { UnderlineTabs } from '@/components/ui/UnderlineTabs'
import { formatCurrency, formatDate } from '@/lib/formatters'
import { type PolicyChangeRow, usePolicyChanges } from '@/models/policyChange'
import { useCurrentUser } from '@/models/user'
import { changeCountsLabel, CLOSED_STATUSES } from './policyChangeCopy'
import { PolicyChangesGate } from './PolicyChangesGate'
import { PolicyChangeStatusBadge } from './PolicyChangeStatusBadge'

enum Tab {
  OPEN = 'open',
  HISTORY = 'history',
}

const COLUMNS = [
  {
    key: 'request',
    header: 'Request',
    hideOnCard: true,
    cell: (row: PolicyChangeRow) => <span className="font-mono text-sm">{row.idShort}</span>,
  },
  { key: 'changes', header: 'Changes', cell: (row: PolicyChangeRow) => changeCountsLabel(row.changeCounts) },
  {
    key: 'effective',
    header: 'Effective from',
    cell: (row: PolicyChangeRow) => formatDate(row.earliestEffectiveDate),
  },
  {
    key: 'price',
    header: 'Price change',
    cell: (row: PolicyChangeRow) =>
      row.proratedChangeTotal === null ? '-' : formatCurrency(Number(row.proratedChangeTotal)),
  },
  {
    key: 'status',
    header: 'Status',
    cell: (row: PolicyChangeRow) => (
      <PolicyChangeStatusBadge
        status={row.status}
        submitted={!!row.submittedAt}
        waitingOnCustomer={row.waitingOnCustomer}
      />
    ),
  },
]

export function PolicyChanges() {
  const navigate = useNavigate()
  const caiDocked = useCaiDocked()
  const { user } = useCurrentUser({ enabled: true })
  const { requests, isLoading, isError } = usePolicyChanges(user?.currentOrg.id)
  const [tab, setTab] = useState(Tab.OPEN)
  const open = requests.filter((request) => !CLOSED_STATUSES.includes(request.status))
  const history = requests.filter((request) => CLOSED_STATUSES.includes(request.status))

  return (
    <PolicyChangesGate>
      <Helmet>
        <title>Policy changes</title>
      </Helmet>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_1fr]">
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-2xl font-semibold">Policy changes</div>
            <p className="mt-1 text-sm text-neutral-600">
              Ask for a change to your policies and follow it through to an updated COI.
            </p>
          </div>
          <OnboardingBannerSlot />
          <UnderlineTabs
            tabs={[
              { value: Tab.OPEN, label: `Open (${open.length})` },
              { value: Tab.HISTORY, label: `History (${history.length})` },
            ]}
            value={tab}
            onValueChange={(value) => setTab(value as Tab)}
          />
          {isLoading && <SkeletonRows rows={4} />}
          {isError && (
            <p className="text-destructive text-sm">
              We couldn&rsquo;t load your policy changes. Refresh to try again.
            </p>
          )}
          {!isLoading && !isError && (
            <ResponsiveTable
              items={tab === Tab.OPEN ? open : history}
              columns={COLUMNS}
              getRowId={(row) => row.id}
              cardTitle={(row) => `Request ${row.idShort}`}
              onRowClick={(row) => navigate(`/insure/policy-changes/${row.id}`)}
              empty={tab === Tab.OPEN ? 'No open policy changes.' : 'No finished policy changes yet.'}
            />
          )}
        </div>

        <div className="flex flex-col gap-4 lg:mt-10">
          {!caiDocked && <AskCai />}
          <Button asChild className="rounded-full">
            <Link to="/insure/policy-changes/new">
              <Plus />
              Request a change
            </Link>
          </Button>
          <Card>
            <CardHeader>
              <CardTitle>How it works</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-neutral-700">
                <li>You tell us what's changing.</li>
                <li>Your insurance broker reviews it and sends it to your insurance providers.</li>
                <li>You accept or decline their quote. Nothing changes until you accept.</li>
                <li>Your policies update and you get a new COI.</li>
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>
    </PolicyChangesGate>
  )
}
